import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import useReveal from "@/hooks/useReveal";

const SERVICES = [
  {
    badge: "Paid Ads",
    title: "Performance Marketing",
    tagline: "Paid ads that generate leads & sales",
    image:
      "/images/services/performance.webp",
    points: [
      "Meta Ads (Facebook & Instagram)",
      "Google Ads (Search, Display, YouTube)",
      "Retargeting campaigns",
      "ROI optimization",
    ],
  },
  {
    badge: "Branding",
    title: "Creative & Branding",
    tagline: "Build attention + trust",
    image:
      "/images/services/branding.webp",
    points: [
      "Brand identity (logo, colors, tone)",
      "Ad creatives (static & motion)",
      "Video ads production",
      "Creative strategy",
    ],
  },
  {
    badge: "Funnels",
    title: "Funnels & Landing Pages",
    tagline: "Turn clicks into customers",
    image:
      "/images/services/funnels.webp",
    points: [
      "Landing page design",
      "Sales funnels",
      "Lead generation systems",
      "Conversion optimization",
    ],
  },
  {
    badge: "Social",
    title: "Social Media Growth",
    tagline: "Authority + audience, compounding",
    image:
      "/images/services/social.webp",
    points: [
      "Social media management",
      "Content planning",
      "Reels / short-form strategy",
      "Engagement & growth",
    ],
  },
  {
    badge: "Local SEO",
    title: "Local SEO & Google Visibility",
    tagline: "Free local leads on autopilot",
    image:
      "/images/services/localseo.webp",
    points: [
      "Google Business Profile",
      "Google Maps ranking",
      "Reviews management",
      "Local SEO",
    ],
  },
  {
    badge: "AI Systems",
    title: "AI Automation",
    tagline: "Leads that close themselves",
    image:
      "/images/services/ai.webp",
    points: [
      "WhatsApp automation",
      "AI chatbots",
      "Lead follow-up systems",
      "CRM integration",
    ],
  },
];

function ServiceCard({ badge, title, tagline, points, image, index }) {
  return (
    <div
      data-testid={`service-card-${index}`}
      className="gmb-card fade-up flex flex-col h-full"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="h-48 w-full overflow-hidden">
        <img
          src={image}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105"
        />
      </div>

      <div className="p-6 flex flex-col flex-1">
        <span className="gmb-badge-yellow self-start">{badge}</span>
        <h3 className="font-display text-xl sm:text-2xl font-bold text-gray-900 mt-3">
          {title} <span className="text-[#B01116]">.</span>
        </h3>
        <p className="text-sm text-gray-500 mt-1">{tagline}</p>

        <ul className="space-y-2 mt-4 mb-6 flex-1">
          {points.map((p) => (
            <li key={p} className="flex items-start text-sm text-gray-600 gap-2">
              <Check className="h-4 w-4 text-[#B01116] mt-0.5 flex-none" strokeWidth={3} />
              <span>{p}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-3 mt-auto">
          <Link
            to="/contact"
            data-testid={`service-card-explore-${index}`}
            className="border-2 border-gray-300 text-gray-800 hover:border-[#B01116] hover:text-[#B01116] px-5 py-2 rounded-full font-accent font-bold text-sm transition-colors"
          >
            Explore Service
          </Link>
          <Link
            to="/contact"
            data-testid={`service-card-cta-${index}`}
            className="bg-[#B01116] hover:bg-[#8A0D11] text-white px-5 py-2 rounded-full font-accent font-bold text-sm transition-colors"
          >
            Request a Callback
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Services() {
  const ref = useReveal();
  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section"
      className="py-16 sm:py-24 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14 fade-up">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#B01116]">
            Choose the Growth Service Your Business Needs!
          </h2>
          <p className="mt-5 text-base text-gray-600 leading-relaxed">
            We don&apos;t just run ads. We architect end-to-end growth systems — from
            first click to closed deal — engineered around your business goals.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} {...s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
